'use client'

import { useState } from 'react'
import Link from 'next/link'
import { BrandLogoIcon } from '@/components/ui/BrandLogo'
import { Loader2, AlertCircle, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react'

export default function ForgotPasswordPage() {
    const [email, setEmail]     = useState('')
    const [loading, setLoading] = useState(false)
    const [done, setDone]       = useState(false)
    const [error, setError]     = useState('')

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setLoading(true)
        try {
            const res = await fetch('/api/auth/forgot-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            })
            if (res.ok) {
                setDone(true)
            } else {
                const data = await res.json()
                setError(data.error || 'Something went wrong. Please try again.')
            }
        } catch {
            setError('Connection error. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className="flex min-h-[100dvh] w-full overflow-hidden bg-white">

            {/* Left panel */}
            <div className="hidden lg:flex w-[52%] bg-slate-950 relative items-center justify-center p-16 overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-accent/15 rounded-full blur-[140px] translate-x-1/4 -translate-y-1/4" />
                    <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/15 rounded-full blur-[140px] -translate-x-1/4 translate-y-1/4" />
                </div>
                <div className="absolute inset-0 opacity-[0.03]"
                    style={{ backgroundImage: 'linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)', backgroundSize: '40px 40px' }} />

                <div className="relative z-10 max-w-md">
                    <div className="w-16 h-16 bg-white rounded-2xl mb-10 shadow-xl flex items-center justify-center">
                        <BrandLogoIcon className="w-11 h-11" />
                    </div>
                    <h1 className="text-4xl font-bold text-white mb-5 leading-tight">
                        Forgot your<br />
                        <span className="text-accent-400">password?</span>
                    </h1>
                    <p className="text-slate-400 text-base leading-relaxed">
                        No worries — enter your work email and we'll send you a secure link to reset it within minutes.
                    </p>
                </div>
            </div>

            {/* Right panel */}
            <div className="flex-1 flex items-center justify-center p-5 pt-20 sm:pt-5 lg:p-16 bg-white relative">

                {/* Mobile logo */}
                <div className="absolute top-6 left-6 lg:hidden flex items-center gap-2">
                    <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                        <BrandLogoIcon className="w-6 h-6" />
                    </div>
                    <span className="font-bold text-slate-800 text-sm">OSTech HRMS</span>
                </div>

                <div className="w-full max-w-sm">

                    {done ? (
                        /* ── Success state ── */
                        <div className="text-center space-y-5">
                            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900">Check your inbox</h2>
                                <p className="mt-2 text-slate-500 text-sm leading-relaxed">
                                    A password reset link has been sent to{' '}
                                    <span className="font-semibold text-slate-700">{email}</span>.
                                    The link expires in 60 minutes.
                                </p>
                            </div>
                            <p className="text-xs text-slate-400">
                                Didn't receive it? Check your spam folder, or{' '}
                                <button
                                    onClick={() => { setDone(false); setEmail('') }}
                                    className="font-semibold text-secondary hover:underline"
                                >
                                    resend
                                </button>.
                            </p>
                            <Link
                                href="/login"
                                className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back to sign in
                            </Link>
                        </div>
                    ) : (
                        /* ── Request form ── */
                        <>
                            <div className="mb-8">
                                <h2 className="text-2xl font-bold text-slate-900">Reset your password</h2>
                                <p className="mt-1.5 text-slate-500 text-sm">
                                    Enter your work email and we'll send you a reset link.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-5">
                                {error && (
                                    <div className="form-error animate-slide-up">
                                        <AlertCircle className="w-4 h-4 shrink-0" />
                                        {error}
                                    </div>
                                )}

                                <div>
                                    <label className="form-label">Email address</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            className="form-input pl-9"
                                            placeholder="you@company.com"
                                            autoComplete="email"
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="btn-primary w-full py-3 text-base"
                                >
                                    {loading
                                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending link…</>
                                        : 'Send reset link'}
                                </button>
                            </form>

                            <Link
                                href="/login"
                                className="mt-6 flex items-center justify-center gap-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" /> Back to sign in
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
